import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Table, Text, Flex, Switch, Box } from '@mantine/core';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export default function UserEmailStreams({
  userLocationId,
  userOrganizationId,
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const url = userLocationId
    ? `/user-locations/${userLocationId}/data-grabber/`
    : `/user-organizations/${userOrganizationId}/data-grabber/`;
  const queryKey = `user${
    userLocationId ? userLocationId : userOrganizationId
  }EmailStreams`;

  const { data: streams, isLoading } = useQuery({
    initialData: [],
    queryKey: [queryKey],
    queryFn: async () =>
      axios
        .post(url, {
          context: `user_email_streams`,
          user_location_id: userLocationId,
          user_organization_id: userOrganizationId,
        })
        .then(({ data }) => {
          return data.response;
        })
        .catch((err) => {
          throw err;
        }),
  });

  const fetchData = () => queryClient.invalidateQueries([queryKey]);

  return (
    <div>
      <Text mb="md" fw={600}>
        {t('mixer:email_preferences')}
      </Text>
      {streams.map((stream) => (
        <Box key={stream.id} mb="lg">
          <EmailStream
            stream={stream}
            userLocationId={userLocationId}
            userOrganizationId={userOrganizationId}
            fetchData={fetchData}
          />
        </Box>
      ))}
      {!isLoading && streams.length === 0 && <Text>No email streams</Text>}
    </div>
  );
}

const EmailStream = ({
  stream,
  userLocationId,
  userOrganizationId,
  fetchData,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [active, setActive] = useState(false);
  const { t } = useTranslation();

  const streamVarietyDescription = {
    marketing: t('mixer:email_stream_marketing_description'),
    news_and_events: t('mixer:email_stream_news_and_events_description'),
    offers: t('mixer:email_stream_offers_description'),
  };

  useEffect(() => {
    setActive(stream.active);
  }, [JSON.stringify(stream)]);

  function onStreamChange() {
    setActive(!active);

    const req = {
      user_email_stream_id: stream.id,
      user_location_id: userLocationId,
      user_organization_id: userOrganizationId,
    };

    const url = userOrganizationId
      ? `/user-organizations/${userOrganizationId}/toggle-email-stream/`
      : `/user-locations/${userLocationId}/toggle-email-stream/`;

    axios
      .post(url, req)
      .then(() => {
        fetchData();
      })
      .catch((err) => {
        fetchData();
        toast.error(err);
      });
  }

  return (
    <>
      <Flex
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'flex-start', md: 'center' }}
        gap="xs"
      >
        <Switch
          checked={active}
          onChange={() => onStreamChange()}
          disabled={stream.can_reactivate ? false : true}
        />
        <div style={{ flexGrow: 1 }}>
          {Object.keys(streamVarietyDescription).includes(
            stream.variety_formatted
          ) ? (
            <Text fw={600}>
              {streamVarietyDescription[stream.variety_formatted]}
            </Text>
          ) : (
            <Text fw={600}>{stream.variety_formatted}</Text>
          )}
        </div>
        <Button
          size="xs"
          onClick={() => {
            setOpen(!isOpen);
          }}
          variant="light"
        >
          {t('mixer:email_preferences_view_events')}
        </Button>
      </Flex>
      {isOpen && (
        <Box
          mt="sm"
          style={{
            overflowY: 'auto',
            maxHeight: '250px',
          }}
        >
          <EmailStreamEvents
            streamId={stream.id}
            userLocationId={userLocationId}
            userOrganizationId={userOrganizationId}
          />
        </Box>
      )}
    </>
  );
};

const EmailStreamEvents = ({
  streamId,
  userLocationId,
  userOrganizationId,
}) => {
  const { t } = useTranslation();
  const { data: events, isLoading } = useQuery({
    initialData: [],
    queryKey: [`emailStreams${streamId}`],
    queryFn: async () =>
      axios
        .post(
          userLocationId
            ? `/user-locations/${userLocationId}/data-grabber/`
            : `/user-organizations/${userOrganizationId}/data-grabber/`,
          {
            context: `user_email_stream_events`,
            user_location_id: userLocationId,
            user_email_stream_id: streamId,
            user_organization_id: userOrganizationId,
          }
        )
        .then(({ data }) => {
          return data.response;
        })
        .catch((err) => {
          throw err;
        }),
  });

  return (
    <Table striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t('common:action_singular')}</Table.Th>
          <Table.Th>{t('common:date')}</Table.Th>
          <Table.Th>{t('common:by')}</Table.Th>
          <Table.Th>{t('common:comment')}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {events.map((event) => (
          <Table.Tr key={event.id}>
            <Table.Td>{event.variety_formatted}</Table.Td>
            <Table.Td>{new Date(event.created_at).toLocaleString()}</Table.Td>
            <Table.Td>{event.by_hash ? event.by_hash.full_name : ''}</Table.Td>
            <Table.Td>
              {event.deactivation_reason_formatted
                ? event.deactivation_reason_formatted
                : ''}
            </Table.Td>
          </Table.Tr>
        ))}
        {events.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={5}>
              {t('mixer:email_events_no_results')}
            </Table.Td>
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  );
};
